import type { LegalDocument } from './documents';
import { LEGAL_LAST_UPDATED } from './documents';

export interface LegalDocumentViewProps {
  doc: LegalDocument;
}

/**
 * Renders one policy document.
 *
 * Plain prose, deliberately: no cards, no badges, no accordions. A collapsed
 * legal document is a legal document nobody read, and "we published it" is a
 * weaker claim when the words were behind a click. Everything is open, in order.
 *
 * The width comes from the shell's ordinary reading column rather than a wider
 * one of its own. A wider measure does read better for nine hundred words — but
 * the <h1> belongs to the shell, so widening only the body left the title
 * hanging off to the left of its own document, which looks like a broken layout
 * rather than a considered one. One column, heading and text aligned.
 *
 * Headings are <h2> — the page's <h1> is the document title, supplied by the
 * shell — so a screen-reader user can jump between clauses instead of arrowing
 * through nine hundred words of Privacy Policy.
 */
// `doc`, not `document`: the parameter would otherwise shadow the global one.
export function LegalDocumentView({ doc }: LegalDocumentViewProps) {
  return (
    <article>
      <p className="text-sm text-ink-subtle">
        Last updated {doc.lastUpdated ?? LEGAL_LAST_UPDATED}
      </p>

      <div className="mt-6 flex flex-col gap-8">
        {doc.sections.map((section, index) => (
          // Index as key: this list is static module data, never reordered or
          // filtered, and a heading is not guaranteed unique or even present.
          <section key={index} className="flex flex-col gap-3">
            {section.heading ? (
              <h2 className="text-lg font-semibold tracking-tight text-ink">{section.heading}</h2>
            ) : null}

            {section.paragraphs?.map((paragraph) => (
              // `text-ink-muted` rather than full `text-ink`: this is a long read,
              // and body prose at maximum contrast for nine hundred words is
              // harder on the eye than the headings it competes with.
              <p
                key={paragraph.slice(0, 48)}
                className="text-[0.9375rem] leading-relaxed text-ink-muted"
              >
                {paragraph}
              </p>
            ))}

            {section.items ? (
              // Markers kept (list-disc) rather than styled away: these are the
              // enumerated clauses, and the marker is what tells a reader where
              // one obligation ends and the next begins. Keeping the default
              // list-style also sidesteps the Safari/VoiceOver bug that costs
              // `list-none` its list semantics — see Stepper.
              <ul className="flex list-disc flex-col gap-3 pl-5 text-[0.9375rem] leading-relaxed text-ink-muted">
                {section.items.map((item) => (
                  <li key={item.slice(0, 48)}>{item}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </div>
    </article>
  );
}
